import React, { useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { getNivoCommonProps } from '../../utils/nivoTheme';
import './ProjectBreakdownDonut.css';

/**
 * Compact Nivo donut for project revenue/cost/hours mix.
 *
 * @param {Object} props
 * @param {string} props.title - Section title above the chart
 * @param {Array<{id: string, label: string, value: number}>} props.slices - Raw slices (zeros filtered)
 * @param {function} props.formatValue - Formats absolute values in legend/tooltip/center
 * @param {string} [props.emptyLabel] - Shown when there is no data
 */
const ProjectBreakdownDonut = ({ title, slices, formatValue, emptyLabel = '-' }) => {
  const data = useMemo(
    () => (slices || []).filter((s) => s != null && Number(s.value) > 0),
    [slices]
  );

  const total = useMemo(
    () => data.reduce((sum, s) => sum + Number(s.value), 0),
    [data]
  );

  const commonProps = getNivoCommonProps();

  return (
    <div className="project-breakdown-donut">
      <div className="project-breakdown-donut-title">{title}</div>
      <div className="project-breakdown-donut-chart">
        {data.length === 0 || total <= 0 ? (
          <div className="project-breakdown-donut-empty">{emptyLabel}</div>
        ) : (
          <ResponsivePie
            {...commonProps}
            data={data}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            innerRadius={0.62}
            padAngle={0.6}
            cornerRadius={3}
            activeOuterRadiusOffset={4}
            borderWidth={1}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.2]],
            }}
            enableArcLinkLabels={false}
            enableArcLabels={false}
            isInteractive
            tooltip={({ datum }) => (
              <div className="project-breakdown-donut-tooltip">
                <span
                  className="project-breakdown-donut-tooltip-swatch"
                  style={{ backgroundColor: datum.color }}
                />
                <span>{datum.label}:</span>
                <strong>{formatValue(datum.value)}</strong>
                <span className="project-breakdown-donut-tooltip-pct">
                  ({total > 0 ? ((datum.value / total) * 100).toFixed(0) : 0}%)
                </span>
              </div>
            )}
          />
        )}
      </div>
      {data.length > 0 && total > 0 && (
        <div className="project-breakdown-donut-total">{formatValue(total)}</div>
      )}
      {data.length > 0 && total > 0 && (
        <ul className="project-breakdown-donut-legend">
          {data.map((slice, index) => {
            const color = commonProps.colors[index % commonProps.colors.length];
            const pct = ((Number(slice.value) / total) * 100).toFixed(0);
            return (
              <li key={slice.id} className="project-breakdown-donut-legend-item">
                <span
                  className="project-breakdown-donut-legend-swatch"
                  style={{ backgroundColor: color }}
                />
                <span className="project-breakdown-donut-legend-label">{slice.label}</span>
                <span className="project-breakdown-donut-legend-value">
                  {pct}%
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProjectBreakdownDonut;
